import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaletteStore } from '@/stores/palette';
import { useFeaturesStore } from '@/stores/features';
import { PALETTE_ENTRIES, type PaletteEntry } from '@/data/paletteGroups';
import { Icon } from '@/design-system/icons/Icon';
import { Kbd } from '@/components/atoms/Kbd';
import { cx } from '@/utils/cx';
import { includesNorm } from '@/utils/norm';
import { useShortcut } from '@/hooks/useShortcut';
import { useAuthStore } from '@/stores/auth';
import { BETA_MODE, isBetaRouteAllowedForRole } from '@/app/BetaRouteGate';

const groupOrder = ['Principal', 'Descobrir', 'Ferramentas', 'Gestão'];

export const CommandPalette = () => {
  const open = usePaletteStore((s) => s.open);
  const query = usePaletteStore((s) => s.query);
  const setQuery = usePaletteStore((s) => s.setQuery);
  const hide = usePaletteStore((s) => s.hide);
  const toggle = usePaletteStore((s) => s.toggle);
  const flags = useFeaturesStore((s) => s.flags);
  const role = useAuthStore((s) => s.user?.role ?? 'visitor');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [cursor, setCursor] = useState(0);

  useShortcut({ key: 'k', meta: true }, (e) => {
    e.preventDefault();
    toggle();
  });

  useEffect(() => {
    if (open) {
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const filtered: PaletteEntry[] = useMemo(() => {
    return PALETTE_ENTRIES.filter((e) => {
      if (e.feature && !flags[e.feature] && !(BETA_MODE && isBetaRouteAllowedForRole(e.route, role))) return false;
      if (BETA_MODE && !isBetaRouteAllowedForRole(e.route, role)) return false;
      if (!query) return true;
      return includesNorm(e.label, query);
    });
  }, [query, flags, role]);

  const grouped = useMemo(() => {
    const map = new Map<string, PaletteEntry[]>();
    for (const g of groupOrder) map.set(g, []);
    filtered.forEach((e) => map.get(e.group)?.push(e));
    return [...map.entries()].filter(([, list]) => list.length > 0);
  }, [filtered]);

  const onPick = (e: PaletteEntry) => {
    hide();
    navigate(e.route);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return hide();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(filtered.length - 1, c + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === 'Enter') {
        const pick = filtered[cursor];
        if (pick) {
          hide();
          navigate(pick.route);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, cursor, hide, navigate]);

  let runningIndex = 0;
  return (
    <div className={cx('palette-scrim', open && 'open')} onClick={hide}>
      <div className="palette" role="dialog" aria-label="Busca" onClick={(e) => e.stopPropagation()}>
        <div className="palette-input">
          <Icon name="search" size={18} className="ic" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            placeholder="Para onde vamos? Digite uma tela, associação ou strain…"
            autoComplete="off"
          />
          <Kbd>esc</Kbd>
        </div>
        <div className="palette-list">
          {grouped.length === 0 && (
            <div style={{ padding: 20, color: 'var(--ink-3)' }}>Nada encontrado.</div>
          )}
          {grouped.map(([group, list]) => (
            <div key={group}>
              <div className="palette-group">{group}</div>
              {list.map((e) => {
                const isCursor = runningIndex === cursor;
                const item = (
                  <button
                    key={e.id}
                    type="button"
                    className={cx('palette-item', isCursor && 'cursor')}
                    onMouseEnter={() => setCursor(runningIndex)}
                    onClick={() => onPick(e)}
                  >
                    <Icon name={e.icon} size={18} className="ic" />
                    <span className="label">{e.label}</span>
                    <span className="hint">{e.route}</span>
                  </button>
                );
                runningIndex += 1;
                return item;
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
